import React from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { useLanguage } from '../../shared/contexts/LanguageContext';
import { ROUTES, localizedPath, stripLanguage } from '../../shared/constants/routes';
import { PageUseCase } from '../../corelogic/usecases/PageUseCase';
import { ContentUseCase, ExtendablePage } from '../../corelogic/usecases/ContentUseCase';
import { NotFoundPage } from './NotFoundPage';

const pageUseCase = new PageUseCase();
const contentUseCase = new ContentUseCase();

/**
 * A route only counts as existing while its page is switched on. Without that
 * check a switched-off page would be redirected to its own address, which has
 * no route, which lands back here.
 */
const isServed = (path: string): boolean => {
  const route = ROUTES.find((entry) => entry.path === path);
  if (route) {
    return !route.page || contentUseCase.isPublished(route.page as ExtendablePage);
  }
  return pageUseCase.isPagePath(path);
};

/**
 * Catch-all for URLs that carry no language prefix (legacy links, local dev)
 * and for addresses that do not exist.
 *
 * A path that leads somewhere is sent to its language-prefixed form. Anything
 * else gets the not-found page rather than a redirect to the start page: a
 * redirect leaves the visitor wondering what happened, and tells search
 * engines the address is fine when it is not.
 */
export const LanguageRedirect: React.FC = () => {
  const { language } = useLanguage();
  const location = useLocation();

  const { path } = stripLanguage(location.pathname);

  if (!isServed(path)) {
    return <NotFoundPage />;
  }

  return (
    <Navigate to={`${localizedPath(path, language)}${location.search}${location.hash}`} replace />
  );
};
